import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { SearchInput } from '@/components/ui/search-input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Pagination } from '@/components/ui/pagination';
import { Loader2 } from 'lucide-react';
import { usePermissions } from '@/common/hooks/usePermissions';
import {
  documentsService,
  DocumentAccessHistoryItem,
} from '@/features/documents/services/documents.service';

export const Route = createFileRoute('/_authenticated/documents-access-history')({
  component: DocumentsAccessHistoryPage,
});

function DocumentsAccessHistoryPage() {
  const { hasPermission } = usePermissions();
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [search, setSearch] = useState('');

  const canManage = hasPermission('documents.manage');

  const { data: historyData, isLoading } = useQuery({
    queryKey: ['documents-access-history', page, limit, search],
    queryFn: async () => {
      return await documentsService.getAccessHistory({
        page,
        limit,
        search: search || undefined,
      });
    },
    enabled: canManage,
  });

  const history: DocumentAccessHistoryItem[] = historyData?.items || [];
  const total = historyData?.total || 0;
  const totalPages = historyData?.totalPages || 0;

  if (!canManage) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold">Acesso Negado</h1>
          <p className="text-muted-foreground mt-2">
            Você não tem permissão para acessar esta página.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold">Histórico de Acessos aos Documentos</h1>
        <p className="text-muted-foreground mt-2">
          Visualize todos os acessos realizados aos documentos do sistema.
        </p>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Histórico de Acessos</CardTitle>
            <div className="w-64">
              <SearchInput
                placeholder="Buscar por nome ou email..."
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1); // Resetar para primeira página ao buscar
                }}
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="py-8 text-center">
              <Loader2 className="h-6 w-6 animate-spin mx-auto text-muted-foreground" />
            </div>
          ) : !history || history.length === 0 ? (
            <div className="py-8 text-center text-muted-foreground">
              {search
                ? 'Nenhum resultado encontrado para a busca.'
                : 'Nenhum acesso registrado ainda.'}
            </div>
          ) : (
            <>
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Usuário</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Documento</TableHead>
                      <TableHead>Data/Hora do Acesso</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {history.map((item) => (
                      <TableRow key={item.id}>
                        <TableCell className="font-medium">
                          {item.userName || item.userEmail}
                        </TableCell>
                        <TableCell>{item.userEmail}</TableCell>
                        <TableCell>{item.documentName}</TableCell>
                        <TableCell>
                          {new Date(item.accessedAt).toLocaleString('pt-BR', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {totalPages > 1 && (
                <div className="mt-4">
                  <Pagination
                    total={total}
                    page={page}
                    limit={limit}
                    onPageChange={setPage}
                  />
                </div>
              )}

              <div className="mt-4 text-sm text-muted-foreground text-center">
                Mostrando {history.length} de {total} acesso(s)
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}


